import round from "lodash/round";
import React, {
  CSSProperties,
  FunctionComponent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
} from "react";
import useElementClientBoundingRect from "../../../lib/useElementClientBoundingRect";
import { isTouchDevice } from "../../../utils/customization/device";
import "./style.scss";

interface Props {
  children: ReactNode;
  onPan: (relativeDeltaX: number, relativeDeltaY: number) => void;
  onZoom: (scale: number, relativeOffsetX: number, relativeOffsetY: number) => void;
  style?: CSSProperties;
}

const PanZoom: FunctionComponent<Props> = ({ children, onPan, onZoom, style }) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const elementClientRect = useElementClientBoundingRect(elementRef);

  const onWheelTick = useRef<boolean>(false);

  const onWheel = useCallback(
    (event: WheelEvent) => {
      if (!elementClientRect) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();

      if (!onWheelTick.current) {
        const { ctrlKey, deltaX, deltaY, pageX, pageY } = event;

        window.requestAnimationFrame(() => {
          const { left, top, width, height } = elementClientRect;

          if (ctrlKey) {
            const deltaScale = round(Math.exp(-deltaY / 100), 2);
            const relativeX = round((pageX - left) / width, 2);
            const relativeY = round((pageY - top) / height, 2);
            onZoom(deltaScale, relativeX, relativeY);
          } else {
            onPan(deltaX / width, deltaY / height);
          }

          onWheelTick.current = false;
        });

        onWheelTick.current = true;
      }
    },
    [elementClientRect, onPan, onZoom]
  );

  // Safari
  // TODO: Support position
  const onGestureTick = useRef<boolean>(false);
  const prevScale = useRef<number>(1);
  const onGesture = useCallback(
    (event: GestureEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (event.type === "gesturestart") {
        prevScale.current = 1;
      }

      if (!onGestureTick.current || event.type === "gestureend") {
        const { scale } = event;

        window.requestAnimationFrame(() => {
          onZoom(round(scale / prevScale.current, 2), 0.5, 0.5);

          prevScale.current = scale;
          onGestureTick.current = false;
        });

        onGestureTick.current = true;
      }
    },
    [onZoom]
  );

  const dragging = useRef<boolean>(false);
  const lastPosition = useRef<{ pageX: number; pageY: number } | null>(null);

  const onMouseTick = useRef<boolean>(false);

  const handlePicturePosition = useCallback(
    (event: { type: string; pageX: number; pageY: number }) => {
      if (!elementClientRect) {
        return;
      }

      if (event.type === "mousedown" || event.type === "touchstart") {
        dragging.current = true;
        lastPosition.current = event;
        document.body.classList.add("panZoomIsGrabbing");
      }

      if (!dragging.current) {
        return;
      }

      if (!onMouseTick.current || event.type === "mouseup" || event.type === "touchend") {
        const { pageX, pageY, type: eventType } = event;

        window.requestAnimationFrame(() => {
          if (lastPosition.current) {
            const { pageX: lastPageX, pageY: lastPageY } = lastPosition.current;
            const { width, height } = elementClientRect;

            const deltaX = lastPageX - pageX;
            const deltaY = lastPageY - pageY;

            onPan(deltaX / width, deltaY / height);
          }

          if (eventType === "mouseup" || event.type === "touchend") {
            dragging.current = false;
            document.body.classList.remove("panZoomIsGrabbing");
            lastPosition.current = null;
          } else {
            lastPosition.current = { pageX, pageY };
          }

          onMouseTick.current = false;
        });

        onMouseTick.current = true;
      }
    },
    [elementClientRect, onPan]
  );

  const onMouse = useCallback(
    (event: MouseEvent) => {
      event.preventDefault();

      handlePicturePosition(event);
    },
    [handlePicturePosition]
  );

  const lastTouchDist = useRef<number | null>(null);
  const onTouch = useCallback(
    (event: TouchEvent) => {
      event.preventDefault();

      if (event.touches.length > 1) {
        window.requestAnimationFrame(() => {
          const dist = Math.hypot(
            event.touches[0].pageX - event.touches[1].pageX,
            event.touches[0].pageY - event.touches[1].pageY
          );

          if (lastTouchDist.current) {
            onZoom(round(dist / lastTouchDist.current, 2), 0.5, 0.5);
          }

          if (event.type === "touchstart") {
            lastTouchDist.current = dist;
          }
        });
      } else {
        handlePicturePosition({
          pageX: event.changedTouches[0].pageX,
          pageY: event.changedTouches[0].pageY,
          type: event.type,
        });
      }
    },
    [handlePicturePosition, lastTouchDist, onZoom]
  );

  const addEvents = useCallback(
    (element: HTMLDivElement) => {
      if (isTouchDevice()) {
        element.addEventListener("touchstart", onTouch);
        element.addEventListener("touchmove", onTouch);
        element.addEventListener("touchend", onTouch);
      }

      element.addEventListener("mousedown", onMouse);
      document.addEventListener("mousemove", onMouse);
      document.addEventListener("mouseup", onMouse);

      element.addEventListener("wheel", onWheel);

      element.addEventListener("gesturestart", onGesture);
      element.addEventListener("gesturechange", onGesture);
      element.addEventListener("gestureend", onGesture);
    },
    [onGesture, onMouse, onTouch, onWheel]
  );

  const removeEvents = useCallback(
    (element: HTMLDivElement) => {
      if (isTouchDevice()) {
        element.removeEventListener("touchstart", onTouch);
        element.removeEventListener("touchmove", onTouch);
        element.removeEventListener("touchend", onTouch);
      }

      element.removeEventListener("mousedown", onMouse);
      document.removeEventListener("mousemove", onMouse);
      document.removeEventListener("mouseup", onMouse);

      element.removeEventListener("wheel", onWheel);

      element.removeEventListener("gesturestart", onGesture);
      element.removeEventListener("gesturechange", onGesture);
      element.removeEventListener("gestureend", onGesture);
    },
    [onGesture, onMouse, onTouch, onWheel]
  );

  useEffect(() => {
    const element = elementRef.current;
    if (!element) {
      return;
    }

    addEvents(element);

    return () => {
      removeEvents(element);
    };
  }, [addEvents, removeEvents]);

  return (
    <div className="PanZoom" ref={elementRef} style={style}>
      {children}
    </div>
  );
};

export default PanZoom;
